import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method } from "@stencil/core";
import { isRovingTabEvent, moveRovingTabIndex } from "../../utils/roving-tabindex";

/**
 * @slot - The items of the list.
 */
@Component({
    tag: "uic-list",
    styleUrl: "list.scss"
})
export class List implements ComponentInterface {
    @Element() host: HTMLUicListElement;

    /**
     * Fires when an item is selected.
     */
    @Event({ eventName: "uicSelect" }) select: EventEmitter;

    /**
     * Sets focus to a selected item to enable keyboard navigation.
     */
    @Method()
    async setFocus() {
        this.host.addEventListener("keydown", (e) => this.handleKeydown(e));

        const selected = this.getSelectedItem();
        if (selected) {
            selected.tabIndex = 0; // make sure we can focus
            selected.focus();
        }
    }

    /**
     * Removes focus from the list.
     */
    @Method()
    async removeFocus() {
        this.host.removeEventListener("keydown", (e) => this.handleKeydown(e));

        const item = this.getSelectedItem();
        if (item) {
            item.tabIndex = -1;
        }
    }

    private handleKeydown(event: KeyboardEvent) {
        if (event.key === "Enter" || event.key === " ") {
            const target = event.target as HTMLElement;
            const item = target.closest("[uic-item]") as HTMLElement;

            if (item) {
                this.setSelectedItem(item);
                event.preventDefault();
            }
            return;
        }

        if (isRovingTabEvent(event)) {
            moveRovingTabIndex(event, this.getItems());
        }
    }

    private setSelectedItem(item: HTMLElement) {
        this.select.emit(item);
    }

    private getItems() {
        return [...this.host.querySelectorAll("[uic-item]:not([disabled]):not(.uic-hidden)")] as HTMLElement[];
    }

    private getSelectedItem() {
        return (this.host.querySelector("[uic-item][selected]:not(.uic-hidden)") ||
            this.host.querySelector("[uic-item]:not(.uic-hidden)")) as HTMLElement;
    }

    render() {
        return (
            <Host>
                <slot></slot>
            </Host>
        );
    }
}
